import React, { createContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import getThemeLookup from '../selectors/getThemeLookup';
import combineStyles from '../utils/combineStyles';
import HeaderBranding from './HeaderBranding';
import '../styles/HeaderBar.css';
import Hamburger from './Hamburger';
import { setCurrentDrawer } from '../slices/currentDrawer';
import { LEFT_MENU_DRAWER } from '../drawers/LeftMenuDrawer';
import MenuLocationDetails from './MenuLocationDetails';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import MenuSearchButton from './MenuSearchButton';
import { useLocation } from 'react-router-dom';
import { INITIAL_SCREEN_ROUTE, MENU_SCREEN_ROUTE } from '../hooks/useRoutes';
import Text from './Text';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { MENU_SEARCH_MODAL } from '../modals/MenuSearchModal';
import MenuFilterButton from './MenuFilterButton';
import { setCurrentModalId } from '../slices/currentModalId';
import { MENU_FILTERS_MODAL_ID } from '../modals/MenuFiltersModal';
import LoyaltyHeaderIcon from './LoyaltyHeaderBar';
import TouchableOpacity from './TouchableOpacity';
import { IoIosArrowBack } from 'react-icons/io';
import history from '../history';

interface IProps {
  themeKey: string;
  showBackOnMobile?: boolean;
}

export const HeaderBarThemeKeyContext = createContext<string | null>(null);

const HeaderBar: React.FC<IProps> = ({ themeKey, showBackOnMobile }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const { pathname } = useLocation();

  const headerContainerStyle = combineStyles(
    styles.container,
    p('headerBar', ['padding', 'backgroundColor', 'boxShadow', 'gap']),
    deviceTypeMobile && styles.mobileContainer,
    p(themeKey, ['padding', 'backgroundColor']),
    p('header', ['padding', 'backgroundColor', 'boxShadow', 'gap']),
  );

  return (
    <div style={headerContainerStyle}>
      <HeaderBarThemeKeyContext.Provider value={themeKey}>
        {showBackOnMobile && deviceTypeMobile ? (
          <TouchableOpacity
            style={styles.backButton}
            onClick={() => {
              history.goBack();
            }}
          >
            <IoIosArrowBack
              style={{ fontSize: 35, color: p('headerBackButton', ['color']).color }}
            />
          </TouchableOpacity>
        ) : (
          <Hamburger onClick={() => dispatch(setCurrentDrawer(LEFT_MENU_DRAWER))} />
        )}

        {pathname === INITIAL_SCREEN_ROUTE && deviceTypeMobile && (
          <Text themeKey="sectionTitleHeader" value={t('title.newOrder')} style={styles.newOrder} />
        )}
        {pathname === MENU_SCREEN_ROUTE && deviceTypeMobile && (
          <div style={styles.menuLocationDetailsWrapper}>
            <MenuLocationDetails />
          </div>
        )}

        {!deviceTypeMobile && <HeaderBranding />}

        {pathname === MENU_SCREEN_ROUTE && deviceTypeMobile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              position: 'absolute',
              right: 15,
              gap: 4,
            }}
          >
            <MenuSearchButton
              toggleSearchActive={() => dispatch(setCurrentModal(MENU_SEARCH_MODAL))}
              containerStyle={{ paddingRight: 0 }}
            />
            <MenuFilterButton
              toggleFilterActive={() => dispatch(setCurrentModalId(MENU_FILTERS_MODAL_ID))}
              containerStyle={{ paddingRight: 0, marginLeft: 10 }}
            />
          </div>
        )}

        {deviceTypeMobile && <LoyaltyHeaderIcon />}
      </HeaderBarThemeKeyContext.Provider>
    </div>
  );
};

const styles: Styles = {
  container: {
    width: '100%',
    height: 55,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 4,
    position: 'relative',
  },
  mobileContainer: {},

  title: {
    textTransform: 'uppercase',
  },
  newOrder: {
    position: 'absolute',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    marginTop: 35,
  },

  menuLocationDetailsWrapper: { position: 'absolute', left: '50%', transform: 'translate(-50%,0)' },
};

export default HeaderBar;
